import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  DeleteSweep as DeleteSweepIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const verdictChip = (verdict) => {
  if (verdict === 'OK') return <Chip size="small" color="success" icon={<CheckCircleIcon />} label="OK" />;
  if (verdict === 'ATTENTION') return <Chip size="small" color="error" icon={<ErrorIcon />} label="ATTENTION" />;
  return <Chip size="small" color="warning" icon={<WarningIcon />} label={verdict || 'UNKNOWN'} />;
};

const envChip = (result) => {
  if (!result || result === 'skipped') return <Chip size="small" variant="outlined" label="—" />;
  if (result === 'success') return <Chip size="small" color="success" label="✓" />;
  return <Chip size="small" color="error" label={result} />;
};

const formatLocalDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const SmokeHistoryPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retentionDays, setRetentionDays] = useState(90);

  // Log viewer modal
  const [logOpen, setLogOpen] = useState(false);
  const [logRow, setLogRow] = useState(null);
  const [logLoading, setLogLoading] = useState(false);

  // Purge dialog
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState(30);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purging, setPurging] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/health/smoke-history', { params: { limit: 50 } });
      setRows(response.data.rows || []);
      setRetentionDays(response.data.retention_days || 90);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load smoke history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openLog = async (row) => {
    setLogOpen(true);
    setLogRow(null);
    setLogLoading(true);
    try {
      const response = await api.get('/health/smoke-history', { params: { limit: 50, include_body: true } });
      const full = (response.data.rows || []).find((r) => r.id === row.id);
      setLogRow(full || row);
    } catch (err) {
      setLogRow({ ...row, body_text: `Failed to load body: ${err.message}` });
    } finally {
      setLogLoading(false);
    }
  };

  const runPurge = async () => {
    if ((purgeConfirm || '').trim().toUpperCase() !== 'PURGE') {
      setError("Type PURGE in the confirmation field to proceed");
      return;
    }
    setPurging(true);
    setError(null);
    try {
      const response = await api.delete('/health/smoke-history', {
        data: { older_than_days: Number(purgeDays), confirmation: 'PURGE' },
      });
      setPurgeOpen(false);
      setPurgeConfirm('');
      await fetchRows();
      alert(`Purged ${response.data.deleted} row(s).`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => navigate('/health-check')} aria-label="Back to health check">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Smoke Test History</Typography>
          <Chip size="small" label={`Rolling ${retentionDays}d retention`} variant="outlined" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={fetchRows} disabled={loading}>
            Refresh
          </Button>
          <Button
            startIcon={<DeleteSweepIcon />}
            color="warning"
            variant="outlined"
            onClick={() => setPurgeOpen(true)}
          >
            Purge
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Verdict</TableCell>
                <TableCell>dev</TableCell>
                <TableCell>staging</TableCell>
                <TableCell>prod</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>SHA</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No smoke runs persisted yet. The first run will land here after the next workflow execution.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{formatLocalDate(r.created_at)}</TableCell>
                    <TableCell>{verdictChip(r.verdict)}</TableCell>
                    <TableCell>{envChip(r.dev_result)}</TableCell>
                    <TableCell>{envChip(r.staging_result)}</TableCell>
                    <TableCell>{envChip(r.prod_result)}</TableCell>
                    <TableCell>{r.event_name || '—'}</TableCell>
                    <TableCell>{r.branch || '—'}</TableCell>
                    <TableCell>
                      <code style={{ fontSize: '0.8em' }}>{(r.head_sha || '').slice(0, 7)}</code>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => openLog(r)}>
                          View log
                        </Button>
                        {r.run_url && (
                          <IconButton
                            size="small"
                            component={MuiLink}
                            href={r.run_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open GH Actions run"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Log viewer */}
      <Dialog open={logOpen} onClose={() => setLogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {logRow?.subject || 'Smoke Run Log'}
          {logRow?.run_url && (
            <MuiLink
              href={logRow.run_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ml: 2, fontSize: '0.8em' }}
            >
              Open GH Actions run ↗
            </MuiLink>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {logLoading ? (
            <CircularProgress />
          ) : (
            <pre
              style={{
                fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                fontSize: '12.5px',
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
            >
              {logRow?.body_text || '(no body persisted)'}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Purge dialog */}
      <Dialog open={purgeOpen} onClose={() => setPurgeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Purge smoke history</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="purge-days-label">Delete rows older than</InputLabel>
              <Select
                labelId="purge-days-label"
                label="Delete rows older than"
                value={purgeDays}
                onChange={(e) => setPurgeDays(e.target.value)}
              >
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
                <MenuItem value={60}>60 days</MenuItem>
                <MenuItem value={0}>ALL rows (full purge)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Type PURGE to confirm"
              value={purgeConfirm}
              onChange={(e) => setPurgeConfirm(e.target.value)}
              autoComplete="off"
              helperText={purgeDays === 0 ? 'This will delete EVERY row, including today.' : `This will delete rows older than ${purgeDays} days.`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeOpen(false)} disabled={purging}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={runPurge}
            disabled={purging || (purgeConfirm || '').trim().toUpperCase() !== 'PURGE'}
          >
            {purging ? <CircularProgress size={16} /> : 'Purge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmokeHistoryPage;
